import { createContext, useCallback, useState } from 'react';
import { Song, getSongs } from '@round/api';
import { FCWithChildren } from '../../../App.types';
import useAbortableEffect from '../../../Hooks/useAbortableEffect';
import { showNotification } from '../../../helpers';

type SongListContextValues = {
    songs: Song[];
    songsCount: number;
    songsLoading: boolean;
    loadMoreSongs: () => void;
    search: string;
    setSearch: (search: string) => void;
};

const PAGE_SIZE = 25;

export const SongListContext = createContext<SongListContextValues | null>(null);
export const SongListContextProvider: FCWithChildren = ({ children }) => {
    const [isInitialized, setIsInitialized] = useState(false);
    const [loading, setLoading] = useState(false);
    const [count, setCount] = useState(0);
    const [data, setData] = useState<Song[]>([]);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);

    const fetchData = useCallback(async (...params: Parameters<typeof getSongs>) => {
        try {
            setLoading(true);
            const {
                data: { results, count, next },
            } = await getSongs(...params);
            setData((data) => [...data, ...results]);
            setCount(count);
            setHasNextPage(Boolean(next));
        } catch (e) {
            if (!(e instanceof Error) || e.name !== 'AbortError') {
                return;
            }
            throw e;
        } finally {
            setLoading(false);
        }
    }, []);

    const reset = useCallback(() => {
        setData([]);
        setCount(0);
        setHasNextPage(false);
        setPage(1);
        setIsInitialized(false);
    }, []);

    const value: SongListContextValues = {
        search,
        setSearch: (search: string) => {
            setPage(1);
            setSearch(search);
            reset();
        },
        songs: data,
        songsCount: count,
        songsLoading: loading,
        loadMoreSongs: async () => {
            if (hasNextPage && !loading) {
                try {
                    await fetchData({ search, page: page + 1, page_size: PAGE_SIZE });
                    setPage((page) => page + 1);
                } catch {
                    showNotification("Couldn't load songs", 'error');
                }
            }
        },
    };

    const init = useCallback(
        async (requestInit: RequestInit) => {
            try {
                await fetchData({ page, page_size: PAGE_SIZE, search }, requestInit);
                setIsInitialized(true);
            } catch {
                showNotification("Couldn't load songs", 'error');
            }
        },
        [fetchData, page, search]
    );

    useAbortableEffect(
        (signal) => {
            if (!isInitialized) {
                init({ signal });
            }
        },
        [init, isInitialized]
    );

    return <SongListContext.Provider value={value}>{children}</SongListContext.Provider>;
};
